import { ComponentRef, Directive, OnDestroy, Type, ViewContainerRef } from '@angular/core';
import { DynamicComponentService } from './dynamic-component.service';

/**
 * Directive for hosting dynamically created components.
 * Provides a declarative way to specify component containers in templates
 * and handles component lifecycle management automatically.
 *
 * Usage:
 * <div appComponentHost #hostRef="appComponentHost"></div>
 */
@Directive({
  selector: '[appComponentHost]',
  exportAs: 'appComponentHost'
})
export class ComponentHostDirective implements OnDestroy {
  private currentComponent: ComponentRef<any> | null = null;

  constructor(
    public viewContainerRef: ViewContainerRef,
    private dynamicComponentService: DynamicComponentService
  ) { }

  /**
   * Load a component into this host
   *
   * @param componentType The component class to instantiate
   * @param inputs Optional object with input values to set on the component
   * @returns ComponentRef to the created component
   */
  loadComponent<TComponent>(componentType: Type<TComponent>, inputs?: { [key: string]: any }): ComponentRef<TComponent> {
    // Clear any existing component
    this.clear();

    // Create the new component
    this.currentComponent = this.dynamicComponentService.loadComponent(
      this.viewContainerRef,
      componentType,
      inputs
    );

    return this.currentComponent as ComponentRef<TComponent>;
  }

  /**
   * Clear the current component
   */
  clear(): void {
    this.dynamicComponentService.destroyComponent(this.currentComponent);
    this.currentComponent = null;
    this.viewContainerRef.clear();
  }

  /**
   * Clean up on directive destruction
   */
  ngOnDestroy(): void {
    this.clear();
  }

  /**
   * Check if the host has a component loaded
   */
  hasComponent(): boolean {
    return this.currentComponent !== null;
  }

  /**
   * Get the current component instance
   */
  getComponentInstance<TComponent>(): TComponent | null {
    return this.currentComponent ? this.currentComponent.instance as TComponent : null;
  }
}
